import { cloneDeep } from "lodash";
import moment from "moment";
import { CalendarGroup, MenuGroup } from "./SlidesGroups.js";

const escapeHtml = (value = "") =>
  String(value)
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");

const formatEventTime = (rawTime) => {
  if (!rawTime) {
    return "";
  }
  const value = String(rawTime).trim();
  const time = moment(value, ["h:mm A", "h A", "HH:mm", "h:mm"], true);
  if (!time.isValid()) {
    return value.toUpperCase();
  }
  return time.minutes() === 0 ? time.format("h A") : time.format("h:mm A");
};

const normalizeLocation = (rawLocation) => {
  if (rawLocation === null || rawLocation === undefined) {
    return "";
  }
  const value = String(rawLocation).trim();
  if (!value) {
    return "";
  }
  const lower = value.toLowerCase();
  if (lower === "null" || lower === "undefined") {
    return "";
  }
  return value;
};

export const normalizePrintDescription = (value) => {
  if (value == null || value === "") {
    return null;
  }
  const text = String(value)
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n")
    .replace(/<[^>]*>/g, "")
    // Strip zero-width / invisible chars that .trim() does not remove
    .replace(/[\u200b\u200c\u200d\u2060\ufeff]/g, "")
    .replace(/\u00a0/g, " ")
    .split(/\r\n|\r|\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .join("\n")
    .trim();
  return text || null;
};

export const DEFAULT_DAILY_DESCRIPTION_TYPE = { title: true, short: false, long: false };
export const DEFAULT_DAILY_VISIBLE_FIELDS = { title: true, time: true, location: true, short: false, long: false };
export const DEFAULT_BODY_FIELD_ORDER = ["time", "title", "short", "long", "location"];
export const QP_BODY_FIELD_IDS = {
  time: "qpt-field-time",
  title: "qpt-field-title",
  short: "qpt-field-short",
  long: "qpt-field-long",
  location: "qpt-field-location",
};
/** Title + Short fully size-locked (max character limits); still movable. */
export const QP_SIZE_LOCKED_FIELD_KEYS = ["title", "short"];
export const QP_HEIGHT_LOCKED_FIELD_KEYS = ["title", "short"];
export const QP_FIELD_LOCKED_SIZES = {
  title: { width: 420, height: 28 },
  short: { width: 420, height: 28 },
};
/** Default 2D layout: Time | Title | Location on first row, then Short, then Long stacked. */
export const DEFAULT_BODY_FIELD_ROWS = [
  [
    { key: "time", widthWeight: 110 / 680, leftRatio: 0, widthRatio: 110 / 744 },
    { key: "title", widthWeight: 420 / 680, leftRatio: 110 / 744, widthRatio: 420 / 744 },
    { key: "location", widthWeight: 150 / 680, leftRatio: 594 / 744, widthRatio: 150 / 744 },
  ],
  [{ key: "short", widthWeight: 1, leftRatio: 110 / 744, widthRatio: 420 / 744 }],
  [{ key: "long", widthWeight: 1, leftRatio: 110 / 744, widthRatio: 420 / 744 }],
];
/** Canvas body area used to normalize saved field positions for print. */
export const QP_BODY_CONTENT_ORIGIN = 36;
export const QP_BODY_CONTENT_WIDTH = 744;
/** Placeholders within this many px of top are treated as the same horizontal row. */
const BODY_FIELD_ROW_THRESHOLD = 20;
const QP_FIELD_OVERLAP_GAP = 8;
const QP_FIELD_MIN_SIDE_WIDTH = 100;

const rectsOverlap = (a, b) =>
  a.left < b.left + b.width &&
  a.left + a.width > b.left &&
  a.top < b.top + b.height &&
  a.top + a.height > b.top;

/**
 * When a dragged body-field placeholder overlaps another, snap beside it
 * (same row) instead of stacking on top. Prefers right, then left, then below.
 * If allowShrinkWidth, narrows the dragged field to fit remaining horizontal space.
 */
export const resolveQpBodyFieldOverlap = (
  dragged,
  others = [],
  pageWidth = 816,
  pageBorderWidth = 0,
  options = {}
) => {
  const allowShrinkWidth = options.allowShrinkWidth !== false;
  const minWidth = options.minWidth ?? QP_FIELD_MIN_SIDE_WIDTH;
  let left = dragged.left;
  let top = dragged.top;
  let width = dragged.width;
  const { height } = dragged;
  const usableWidth = Math.max(minWidth, (pageWidth || 816) - (pageBorderWidth || 0) * 2);

  for (let iter = 0; iter < 8; iter++) {
    let moved = false;
    for (const other of others) {
      if (!other || !rectsOverlap({ left, top, width, height }, other)) continue;

      const rightLeft = other.left + other.width + QP_FIELD_OVERLAP_GAP;
      const spaceOnRight = usableWidth - rightLeft;
      const spaceOnLeft = other.left - QP_FIELD_OVERLAP_GAP;

      if (spaceOnRight >= Math.min(width, minWidth)) {
        // Prefer sitting to the right — shrink if needed so Short/Long can share a row
        left = rightLeft;
        top = other.top;
        if (allowShrinkWidth && width > spaceOnRight) {
          width = Math.max(minWidth, spaceOnRight);
        }
      } else if (spaceOnLeft >= Math.min(width, minWidth)) {
        const fitWidth =
          allowShrinkWidth && width > spaceOnLeft
            ? Math.max(minWidth, spaceOnLeft)
            : width;
        left = other.left - fitWidth - QP_FIELD_OVERLAP_GAP;
        top = other.top;
        width = fitWidth;
      } else if (allowShrinkWidth && spaceOnRight >= minWidth) {
        left = rightLeft;
        top = other.top;
        width = Math.max(minWidth, spaceOnRight);
      } else if (allowShrinkWidth && spaceOnLeft >= minWidth) {
        width = Math.max(minWidth, spaceOnLeft);
        left = other.left - width - QP_FIELD_OVERLAP_GAP;
        top = other.top;
      } else {
        top = other.top + other.height + QP_FIELD_OVERLAP_GAP;
        left = Math.min(Math.max(0, other.left), Math.max(0, usableWidth - width));
      }
      moved = true;
    }
    if (!moved) break;
  }

  left = Math.max(0, Math.min(left, Math.max(0, usableWidth - width)));
  top = Math.max(0, top);
  return { left, top, width };
};

export const normalizeBodyFieldOrder = (order) => {
  const allowed = new Set(DEFAULT_BODY_FIELD_ORDER);
  const seen = new Set();
  const normalized = [];
  (Array.isArray(order) ? order : []).forEach((key) => {
    if (!allowed.has(key) || seen.has(key)) return;
    seen.add(key);
    normalized.push(key);
  });
  DEFAULT_BODY_FIELD_ORDER.forEach((key) => {
    if (!seen.has(key)) normalized.push(key);
  });
  return normalized;
};

const getBodyFieldPlaceholders = (elements = []) => {
  const idToKey = Object.fromEntries(
    Object.entries(QP_BODY_FIELD_IDS).map(([key, id]) => [id, key])
  );
  return (elements || [])
    .filter((el) => idToKey[el?.id])
    .map((el) => {
      const s = el?.Styles?.[0] || {};
      return {
        key: idToKey[el.id],
        top: s.ElementTop ?? 0,
        left: s.ElementLeft ?? 0,
        width: Math.max(40, s.ElementWidth ?? 100),
        height: Math.max(16, s.ElementHeight ?? 28),
        fontFamily: s.ElementFontFamily || null,
        fontSize: typeof s.ElementFontSize === "number" ? s.ElementFontSize : null,
        fontWeight: s.ElementFontWeight || null,
        fontStyle: s.ElementFontStyle || null,
        textColor: s.ElementTextColor || null,
        textAlign: s.ElementTextAlignment || null,
        letterSpacing: typeof s.ElementLetterSpacing === "number" ? s.ElementLetterSpacing : null,
        lineHeight: typeof s.ElementLineHeight === "number" ? s.ElementLineHeight : null,
      };
    });
};

const FIELD_STYLE_KEYS = [
  "fontFamily",
  "fontSize",
  "fontWeight",
  "fontStyle",
  "textColor",
  "textAlign",
  "letterSpacing",
  "lineHeight",
];

/**
 * Build a 2D body-field layout from canvas placeholders.
 * Fields with similar ElementTop share a row (left→right); rows stack by top.
 * Stores leftRatio/widthRatio so print keeps the saved horizontal placement
 * (e.g. Short centered, Long starting on the left) instead of always packing left.
 * Returns { order, rows: [{ key, widthWeight, leftRatio, widthRatio }[], ...] }.
 */
export const extractBodyFieldLayoutFromElements = (elements = []) => {
  const placeholders = getBodyFieldPlaceholders(elements);
  if (placeholders.length === 0) {
    return {
      order: DEFAULT_BODY_FIELD_ORDER,
      rows: DEFAULT_BODY_FIELD_ROWS.map((row) => row.map((item) => ({ ...item }))),
    };
  }

  const bodyLock = (elements || []).find((el) => el?.id === "qpt-body-lock");
  const origin = bodyLock?.Styles?.[0]?.ElementLeft ?? QP_BODY_CONTENT_ORIGIN;
  const contentWidth = Math.max(
    1,
    bodyLock?.Styles?.[0]?.ElementWidth ?? QP_BODY_CONTENT_WIDTH
  );

  const sorted = [...placeholders].sort((a, b) => (a.top - b.top) || (a.left - b.left));
  const rows = [];
  sorted.forEach((field) => {
    const last = rows[rows.length - 1];
    if (last && Math.abs(field.top - last.anchorTop) <= BODY_FIELD_ROW_THRESHOLD) {
      last.items.push(field);
    } else {
      rows.push({ anchorTop: field.top, items: [field] });
    }
  });

  const layoutRows = rows.map((row) => {
    const items = [...row.items].sort((a, b) => a.left - b.left);
    const totalWidth = items.reduce((sum, item) => sum + item.width, 0) || 1;
    return items.map((item) => {
      const leftRatio = Math.max(0, Math.min(1, (item.left - origin) / contentWidth));
      const widthRatio = Math.max(
        0.05,
        Math.min(1, item.width / contentWidth)
      );
      const style = {};
      FIELD_STYLE_KEYS.forEach((k) => {
        if (item[k] !== null && item[k] !== undefined) style[k] = item[k];
      });
      return {
        key: item.key,
        widthWeight: item.width / totalWidth,
        leftRatio,
        widthRatio,
        ...style,
      };
    });
  });

  const order = normalizeBodyFieldOrder(layoutRows.flatMap((row) => row.map((item) => item.key)));
  return { order, rows: layoutRows };
};

export const extractBodyFieldOrderFromElements = (elements = []) =>
  extractBodyFieldLayoutFromElements(elements).order;

const normalizeLayoutItem = (item) => {
  const normalized = {
    key: item.key,
    widthWeight: typeof item.widthWeight === "number" && item.widthWeight > 0 ? item.widthWeight : 1,
  };
  if (typeof item.leftRatio === "number" && item.leftRatio >= 0) {
    normalized.leftRatio = Math.min(1, item.leftRatio);
  }
  if (typeof item.widthRatio === "number" && item.widthRatio > 0) {
    normalized.widthRatio = Math.min(1, item.widthRatio);
  }
  if (typeof item.fontFamily === "string" && item.fontFamily.trim()) {
    normalized.fontFamily = item.fontFamily.trim();
  }
  if (typeof item.fontSize === "number" && item.fontSize > 0) {
    normalized.fontSize = item.fontSize;
  }
  if (item.fontWeight !== undefined && item.fontWeight !== null && item.fontWeight !== "") {
    normalized.fontWeight = item.fontWeight;
  }
  if (typeof item.fontStyle === "string" && item.fontStyle.trim()) {
    normalized.fontStyle = item.fontStyle.trim();
  }
  if (typeof item.textColor === "string" && item.textColor.trim()) {
    normalized.textColor = item.textColor.trim();
  }
  if (typeof item.textAlign === "string" && item.textAlign.trim()) {
    normalized.textAlign = item.textAlign.trim();
  }
  if (typeof item.letterSpacing === "number") {
    normalized.letterSpacing = item.letterSpacing;
  }
  if (typeof item.lineHeight === "number" && item.lineHeight > 0) {
    normalized.lineHeight = item.lineHeight;
  }
  return normalized;
};

/**
 * Build flex segments (spacers + fields) that preserve saved left/width placement.
 * Falls back to packed widthWeight layout when ratios are missing.
 * Spacers/fields use fixed % widths (no shrink) so long text cannot collapse indentation.
 */
export const buildBodyFieldRowSegments = (row = []) => {
  const items = (Array.isArray(row) ? row : []).filter((item) => item?.key);
  if (items.length === 0) return [];

  const hasPlacement = items.every(
    (item) => typeof item.leftRatio === "number" && typeof item.widthRatio === "number"
  );

  if (!hasPlacement) {
    return items.map((item) => ({
      type: "field",
      key: item.key,
      item,
      flexGrow: items.length > 1 ? item.widthWeight || 1 : 1,
      widthPercent: null,
      marginLeftPercent: null,
    }));
  }

  const sorted = [...items].sort(
    (a, b) => (a.leftRatio ?? 0) - (b.leftRatio ?? 0)
  );

  // Single field on its own row: leading spacer preserves indentation (Long in middle, etc.)
  if (sorted.length === 1) {
    const item = sorted[0];
    const left = Math.max(0, Math.min(0.95, item.leftRatio ?? 0));
    const width = Math.max(0.05, Math.min(1 - left, item.widthRatio ?? 0.5));
    const segments = [];
    if (left > 0.005) {
      segments.push({
        type: "spacer",
        key: `spacer-${item.key}`,
        widthPercent: left * 100,
        marginLeftPercent: null,
      });
    }
    segments.push({
      type: "field",
      key: item.key,
      item,
      flexGrow: 0,
      widthPercent: width * 100,
      marginLeftPercent: null,
    });
    return segments;
  }

  // Multiple fields on one row: spacer columns between absolute left positions
  const segments = [];
  let cursor = 0;

  sorted.forEach((item) => {
    const left = Math.max(0, Math.min(1, item.leftRatio ?? 0));
    const width = Math.max(0.05, Math.min(1 - left, item.widthRatio ?? item.widthWeight ?? 0.2));
    if (left > cursor + 0.005) {
      segments.push({
        type: "spacer",
        key: `spacer-${item.key}`,
        widthPercent: (left - cursor) * 100,
        marginLeftPercent: null,
      });
    }
    segments.push({
      type: "field",
      key: item.key,
      item,
      flexGrow: 0,
      widthPercent: width * 100,
      marginLeftPercent: null,
    });
    cursor = Math.max(cursor, left + width);
  });

  return segments;
};

export const normalizeBodyFieldLayout = (layout) => {
  if (layout?.rows && Array.isArray(layout.rows) && layout.rows.length > 0) {
    const rows = layout.rows
      .map((row) =>
        (Array.isArray(row) ? row : [])
          .filter((item) => item && DEFAULT_BODY_FIELD_ORDER.includes(item.key))
          .map(normalizeLayoutItem)
      )
      .filter((row) => row.length > 0);
    if (rows.length > 0) {
      const seen = new Set();
      const order = [];
      rows.forEach((row) => {
        row.forEach((item) => {
          if (!seen.has(item.key)) {
            seen.add(item.key);
            order.push(item.key);
          }
        });
      });
      DEFAULT_BODY_FIELD_ORDER.forEach((key) => {
        if (seen.has(key)) return;
        // Migrate older layouts missing Time into the first row (left of Title).
        if (key === "time" && rows.length > 0) {
          const first = rows[0];
          const existingWeight = first.reduce((sum, item) => sum + (item.widthWeight || 1), 0) || 1;
          const timeItem = {
            key: "time",
            widthWeight: existingWeight * 0.21,
            leftRatio: 0,
            widthRatio: 110 / 744,
          };
          rows[0] = [
            timeItem,
            ...first.map((item) => ({
              ...item,
              widthWeight: (item.widthWeight || 1) * 0.79,
              leftRatio:
                typeof item.leftRatio === "number"
                  ? Math.min(1, Math.max(item.leftRatio, 110 / 744))
                  : undefined,
            })),
          ];
          order.unshift("time");
        } else if (key === "location" && rows.length > 0) {
          // Migrate older layouts: Location on the right of the first row (with Time | Title).
          const locDefault = DEFAULT_BODY_FIELD_ROWS.flat().find((item) => item.key === "location");
          rows[0] = [
            ...rows[0],
            locDefault
              ? { ...locDefault }
              : {
                  key: "location",
                  widthWeight: 0.2,
                  leftRatio: 594 / 744,
                  widthRatio: 150 / 744,
                },
          ];
          order.push("location");
        } else {
          const defaults = DEFAULT_BODY_FIELD_ROWS.flat().find((item) => item.key === key);
          rows.push([
            defaults
              ? { ...defaults }
              : { key, widthWeight: 1 },
          ]);
          order.push(key);
        }
        seen.add(key);
      });
      return { order: normalizeBodyFieldOrder(order), rows };
    }
  }
  const order = normalizeBodyFieldOrder(layout?.order);
  const orderSet = new Set(order);
  const defaultMatches =
    order.length === DEFAULT_BODY_FIELD_ORDER.length &&
    DEFAULT_BODY_FIELD_ORDER.every((key) => orderSet.has(key));
  return {
    order,
    rows: defaultMatches
      ? DEFAULT_BODY_FIELD_ROWS.map((row) => row.map((item) => ({ ...item })))
      : order.map((key) => [{ key, widthWeight: 1 }]),
  };
};

export const buildDailyEventDescription = (event, descriptionType = {}) => {
  const parts = [];
  if (descriptionType.short && event.shortDescription) {
    parts.push(event.shortDescription);
  }
  if (descriptionType.long && event.longDescription) {
    parts.push(event.longDescription);
  }
  return parts.length > 0 ? parts.join("\n") : null;
};

export const isDailySlideTemplate = (template) =>
  template?.templateType === "Slide" && Array.isArray(template?.dailyEvents);

export const applyDailyPrintExportSettings = (
  template,
  descriptionType = DEFAULT_DAILY_DESCRIPTION_TYPE
) => {
  if (!isDailySlideTemplate(template)) {
    return template;
  }
  return {
    ...template,
    dailyDescriptionType: {
      title: descriptionType?.title !== false,
      time: descriptionType?.time !== false,
      location: descriptionType?.location !== false,
      short: !!descriptionType?.short,
      long: !!descriptionType?.long,
    },
  };
};

const DAILY_EVENT_BASE_WEIGHT = 1;
const DAILY_EVENT_CHARS_PER_LINE = 95;
const DAILY_EVENT_WEIGHT_PER_DESC_LINE = 0.35;
const DAILY_EVENT_MAX_WEIGHT_PER_PAGE = 15;

export const estimateDailyEventWeight = (event, descriptionType = DEFAULT_DAILY_DESCRIPTION_TYPE) => {
  const description = buildDailyEventDescription(event, descriptionType) || "";
  if (!description) {
    return DAILY_EVENT_BASE_WEIGHT;
  }
  const lineCount = description
    .split("\n")
    .reduce((sum, line) => sum + Math.max(1, Math.ceil(line.length / DAILY_EVENT_CHARS_PER_LINE)), 0);
  return DAILY_EVENT_BASE_WEIGHT + lineCount * DAILY_EVENT_WEIGHT_PER_DESC_LINE;
};

export const splitDailyEventsIntoPages = (dailyEvents, descriptionType = DEFAULT_DAILY_DESCRIPTION_TYPE) => {
  const events = dailyEvents || [];
  let weight = 0;
  let splitIndex = events.length;
  for (let i = 0; i < events.length; i += 1) {
    const eventWeight = estimateDailyEventWeight(events[i], descriptionType);
    if (i > 0 && weight + eventWeight > DAILY_EVENT_MAX_WEIGHT_PER_PAGE) {
      splitIndex = i;
      break;
    }
    weight += eventWeight;
  }
  return {
    page1Events: events.slice(0, splitIndex),
    page2Events: events.slice(splitIndex),
  };
};

export const getDailyPrintPageCount = (dailyEvents, descriptionType = DEFAULT_DAILY_DESCRIPTION_TYPE) =>
  splitDailyEventsIntoPages(dailyEvents, descriptionType).page2Events.length > 0 ? 2 : 1;

export const dataUrlToBlob = (dataUrl) => {
  if (!dataUrl || typeof dataUrl !== "string") {
    return null;
  }
  const [header, base64] = dataUrl.split(",");
  if (!base64) {
    return null;
  }
  const mime = header.match(/:(.*?);/)?.[1] || "image/png";
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type: mime });
};

const isLikelyImageSource = (value) => {
  if (typeof value !== "string") return false;
  const src = value.trim();
  if (!src) return false;
  const lower = src.toLowerCase();
  if (lower === "null" || lower === "undefined") return false;
  if (lower.startsWith("data:")) return lower.startsWith("data:image/");
  return true;
};

// generate Page styles for Slide Print
export function generatePageStyle(widthIn, heightIn) {
  // decide a named size when it matches tabloid or letter
  let paper;
  if ((widthIn === 17 && heightIn === 11) || (widthIn === 11 && heightIn === 17)) {
    paper = "tabloid";
  } else if ((widthIn === 11 && heightIn === 8.5) || (widthIn === 8.5 && heightIn === 11)) {
    paper = "letter";
  } else {
    // fallback to explicit custom size
    paper = `${widthIn}in ${heightIn}in`;
  }

  // orientation: landscape whenever width > height
  const orientation = widthIn > heightIn ? "landscape" : "portrait";

  return `
      @page {
        size: ${paper} ${orientation} !important;
        margin: 0;
      }
      @media print {
        html, body {
          margin: 0;
          padding: 0;
          width: ${widthIn}in;
          height: ${heightIn}in;
        }
        .print-container {
          box-sizing: border-box;
          width: ${widthIn}in;
          height: ${heightIn}in;
          margin: 0;
        }
      }
    `;
}

// Extract header layout from canvas Elements (mirrors saveQuickPrintTemplate in CanvasContextProvider)
export const extractQuickPrintHeaderMeta = (elements = []) => {
  const lineEl = elements.find((e) => e.ElementType === "Line");
  const headingEl = elements.find((e) => e.ElementType === "Heading" || e.ElementType === "Text");
  const rawHeading = headingEl?.ElementData ?? "DAILY CALENDAR";

  const dividerTop = lineEl?.Styles?.[0]?.ElementTop ?? 108;
  const dividerHeight = lineEl?.Styles?.[0]?.ElementHeight ?? 3;
  const dividerLeft = lineEl?.Styles?.[0]?.ElementLeft ?? 36;
  const dividerWidth = lineEl?.Styles?.[0]?.ElementWidth ?? 744;
  const dividerColor = lineEl?.Styles?.[0]?.ElementBackgroundColor || "#000000";

  const bodyLockEl = elements.find((e) => e.id === "qpt-body-lock");
  const bodyLockStyle = bodyLockEl?.Styles?.[0];
  const bodyStyle = bodyLockStyle
    ? {
        ElementTop: bodyLockStyle.ElementTop ?? 122,
        ElementLeft: bodyLockStyle.ElementLeft ?? 36,
        ElementWidth: bodyLockStyle.ElementWidth ?? 744,
        ElementHeight: bodyLockStyle.ElementHeight ?? 904,
        ElementBackgroundColor: bodyLockStyle.ElementBackgroundColor || "#ffffff",
        ElementBorderRadius: bodyLockStyle.ElementBorderRadius ?? 0,
        ElementOpacity: bodyLockStyle.ElementOpacity ?? 1,
      }
    : null;

  const headerElements = elements
    .filter((e) => e.id !== "qpt-body-lock" && e.ElementType !== "Line")
    .filter((e) => !Object.values(QP_BODY_FIELD_IDS).includes(e.id))
    .filter(
      (e) =>
        (typeof e.id === "string" && e.id.startsWith("qpt-")) ||
        (e.Styles?.[0]?.ElementTop ?? 0) < dividerTop + dividerHeight
    )
    .sort((a, b) => (a.Styles?.[0]?.ElementZIndex ?? 0) - (b.Styles?.[0]?.ElementZIndex ?? 0))
    .map((e) => {
      const s = e.Styles?.[0] || {};
      return {
        id: e.id || null,
        type: e.ElementType,
        data: e.ElementData || null,
        styles: {
          ElementTop: s.ElementTop ?? 0,
          ElementLeft: s.ElementLeft ?? 0,
          ElementWidth: s.ElementWidth ?? 100,
          ElementHeight: s.ElementHeight ?? 40,
          ElementZIndex: s.ElementZIndex ?? 1,
          ElementRotation: s.ElementRotation ?? 0,
          ElementBackgroundColor: s.ElementBackgroundColor || null,
          ElementBackgroundImage: s.ElementBackgroundImage || null,
          ElementTextColor: s.ElementTextColor || "#000000",
          ElementFontFamily: s.ElementFontFamily || "Lato",
          ElementFontSize: s.ElementFontSize ?? 16,
          ElementFontWeight: s.ElementFontWeight ?? 400,
          ElementFontStyle: s.ElementFontStyle || "normal",
          ElementTextAlignment: s.ElementTextAlignment || "center",
          ElementLetterSpacing: s.ElementLetterSpacing ?? 0,
          ElementLineHeight: s.ElementLineHeight ?? 1.3,
          ElementBorderRadius: s.ElementBorderRadius ?? 0,
          ElementOpacity: s.ElementOpacity ?? 1,
        },
      };
    });

  return {
    heading: rawHeading.replace(/<[^>]*>/g, "").trim(),
    dividerTop,
    dividerHeight,
    dividerLeft,
    dividerWidth,
    dividerColor,
    headerElements,
    bodyStyle,
    bodyFieldLayout: extractBodyFieldLayoutFromElements(elements),
    bodyFieldOrder: extractBodyFieldOrderFromElements(elements),
  };
};

//  Adds Quick Prints Data to selected template like Picture, subtext , color1 and color2

export const placeQuickPrintsDataToTemplate = async (Template, QuickPrintsDataObject, quickPrintTemplateMeta = null) => {
  Template = cloneDeep(Template);
  let logoimage = await fetchImageAsBase64(QuickPrintsDataObject.LogoImage);
  if (logoimage === null) {
    logoimage = QuickPrintsDataObject.LogoImage;
  }
  const normalizedLogo = isLikelyImageSource(logoimage) ? logoimage : null;
  if (Template.templateType === "Calendar") {
    Template.templateData.CalendarStyles.CalendarBorderColor1 = QuickPrintsDataObject.Color1;
    Template.templateData.CalendarStyles.CalendarBorderColor2 = QuickPrintsDataObject.Color2;
    Template.templateData.CalendarHeader.Logo.logoUrl = normalizedLogo;
    Template.templateData.CalendarHeader.Description.text = QuickPrintsDataObject.SubText;
  } else {
    Template.templateData[0].PageBorderColor1 = QuickPrintsDataObject.Color1;
    Template.templateData[0].PageBorderColor2 = QuickPrintsDataObject.Color2;
    for (let elementitem of Template.templateData[0].Elements) {
      if (elementitem.ElementType === "Image") {
        elementitem.Styles[0].ElementBackgroundImage = normalizedLogo;
        elementitem.Styles[0].ElementBackgroundImageUrl = normalizedLogo;
        elementitem.Styles[0].ElementBackgroundImageName = normalizedLogo
          ? QuickPrintsDataObject.LogoImageName
          : null;
      }
    }
    // Prefer live canvas extraction over saved meta so logo/header layout
    // matches the template editor without requiring a re-save. Keep saved-only
    // fields (pageStyle, dividerVisible, etc.) via merge.
    const metaSource =
      quickPrintTemplateMeta || Template._extractedQuickPrintMeta
        ? {
            ...(quickPrintTemplateMeta || {}),
            ...(Template._extractedQuickPrintMeta || {}),
          }
        : null;
    Template.dailyMeta = {
      logo: normalizedLogo,
      color1: QuickPrintsDataObject.Color1,
      color2: QuickPrintsDataObject.Color2,
      subText: QuickPrintsDataObject.SubText,
      pageStyle: metaSource?.pageStyle || null,
      headerElements: metaSource?.headerElements || null,
      bodyStyle: metaSource?.bodyStyle || null,
      dividerTop: metaSource?.dividerTop ?? null,
      dividerHeight: metaSource?.dividerHeight ?? null,
      dividerLeft: metaSource?.dividerLeft ?? null,
      dividerWidth: metaSource?.dividerWidth ?? null,
      dividerColor: metaSource?.dividerColor || null,
      dividerVisible: metaSource?.dividerVisible !== false,
      bodyFieldLayout: (() => {
        try {
          return normalizeBodyFieldLayout(
            metaSource?.bodyFieldLayout || { order: metaSource?.bodyFieldOrder }
          );
        } catch (_err) {
          return normalizeBodyFieldLayout({ order: DEFAULT_BODY_FIELD_ORDER });
        }
      })(),
      bodyFieldOrder: normalizeBodyFieldOrder(
        metaSource?.bodyFieldLayout?.order || metaSource?.bodyFieldOrder
      ),
    };
    if (metaSource && Object.prototype.hasOwnProperty.call(metaSource, "heading") && !Template.dailyHeading) {
      Template.dailyHeading = metaSource.heading ?? "";
    }
  }
  return Template;
};

const blobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error);
    reader.onloadend = () => resolve(reader.result);
    reader.readAsDataURL(blob);
  });

const imageUrlToDataUrlViaCanvas = (url) =>
  new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = Math.max(1, img.naturalWidth);
        canvas.height = Math.max(1, img.naturalHeight);
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          resolve(null);
          return;
        }
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL("image/png"));
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });

async function fetchImageAsBase64(url) {
  try {
    if (!url || typeof url !== "string") {
      return null;
    }
    const trimmed = url.trim();
    const normalized = trimmed.toLowerCase();
    if (!normalized || normalized === "null" || normalized === "undefined") {
      return null;
    }
    if (normalized.startsWith("data:")) {
      return trimmed;
    }

    try {
      const res = await fetch(trimmed, { cache: "no-cache", credentials: "omit" });
      if (res.ok) {
        const contentType = (res.headers.get("content-type") || "").split(";")[0].trim();
        if (!contentType || contentType.startsWith("image/") || contentType === "application/octet-stream") {
          const blob = await res.blob();
          if (blob.size > 0) {
            return await blobToDataUrl(blob);
          }
        }
      }
    } catch (err) {
      console.error("fetchImageAsBase64:", err);
    }

    return await imageUrlToDataUrlViaCanvas(trimmed);
  } catch (err) {
    console.error("fetchImageAsBase64:", err);
    return null;
  }
}

/**
 * Swap remote <img> src / CSS background-image for data URLs on the node that
 * html-to-image will rasterize. Live preview can keep the remote URL; capture
 * must not, or CORS/network failures silently drop the logo from PDF/PNG.
 */
export async function inlineRemoteImagesForHtmlCapture(node) {
  if (!node) return () => {};
  const restores = [];

  const toDataUrl = async (url) => {
    if (!url || /^data:/i.test(url) || url === "none") return null;
    return fetchImageAsBase64(url);
  };

  const imgs = Array.from(node.querySelectorAll("img"));
  await Promise.all(
    imgs.map(async (img) => {
      const src = img.getAttribute("src") || "";
      const dataUrl = await toDataUrl(src);
      if (!dataUrl || dataUrl === src) return;
      restores.push(() => img.setAttribute("src", src));
      img.setAttribute("src", dataUrl);
    })
  );

  const elements = [node, ...Array.from(node.querySelectorAll("*"))];
  await Promise.all(
    elements.map(async (el) => {
      if (!el?.style) return;
      const value = el.style.backgroundImage;
      if (typeof value !== "string" || !value.includes("url(")) return;
      const match = /url\(\s*(['"]?)([^'")]+)\1\s*\)/.exec(value);
      const url = match?.[2];
      const dataUrl = await toDataUrl(url);
      if (!dataUrl || dataUrl === url) return;
      restores.push(() => {
        el.style.backgroundImage = value;
      });
      el.style.backgroundImage = `url("${dataUrl}")`;
    })
  );

  return () => restores.forEach((restore) => restore());
}

export const placeSpeak2ContentOnTemplate = (
  ActiveTemplate,
  speak2Data,
  CalendarTypes,
  selectedContentType = "Calendar",
  templateMeta = null
) => {
  if (ActiveTemplate.templateType === "Calendar") {
    return placeSpeak2ContentToCalendarTempalte(
      ActiveTemplate,
      speak2Data,
      CalendarTypes,
      selectedContentType
    );
  } else {
    return placeSpeak2ContentToSlideTemplate(
      ActiveTemplate,
      speak2Data,
      selectedContentType,
      templateMeta
    );
  }
};

const getPrintoutPeriodLabel = (template) => {
  const start = moment(template?.startDate, ["YYYY-MM-DD", "MM/DD/YYYY"], true);
  const end = moment(template?.endDate, ["YYYY-MM-DD", "MM/DD/YYYY"], true);
  if (!start.isValid() || !end.isValid()) {
    return null;
  }
  const daySpan = end.diff(start, "days") + 1;
  if (daySpan <= 1) {
    return "Daily";
  }
  if (daySpan <= 7) {
    return "Weekly";
  }
  return "Monthly";
};

const getTabHeaderLabel = (template, selectedContentType = "Calendar") => {
  const baseLabel = selectedContentType === "Menu" ? "Menu" : "Calendar";
  const period = getPrintoutPeriodLabel(template);
  return period ? `${period} ${baseLabel}` : baseLabel;
};

// move the all day menu to end

const handeMenuContentMealTypeAll = (data) => {
  const groups = data.reduce((acc, item) => {
    const key = moment.unix(item.DateAdded / 1000).format("YYYY-MM-DD");
    (acc[key] = acc[key] || []).push(item);
    return acc;
  }, {});

  const result = Object.values(groups).flatMap((group) => {
    const others = group.filter((x) => x.MealType !== "All day");
    const allDay = group.filter((x) => x.MealType === "All day");
    return [...others, ...allDay];
  });

  return result;
};

// merge same MealTypes

const mergeMealTypes = (data) => {
  const merged = Object.values(
    data.reduce((acc, item) => {
      if (!acc[item.MealType]) {
        // start a new group, keep all other fields the same but accumulate MealsList
        acc[item.MealType] = { ...item, MealsList: [item.MealsList] };
      } else {
        // existing group—just push onto the array
        acc[item.MealType].MealsList.push(item.MealsList);
      }
      return acc;
    }, {})
  ).map((item) => ({
    ...item,
    MealsList: item.MealsList.join(", "),
  }));
  return merged;
};

const buildMenuCellHtml = (mealType, mealsList) => {
  const mealLabel = escapeHtml(mealType || "");
  const mealText = escapeHtml(String(mealsList || "").trim());
  if (!mealLabel && !mealText) {
    return "";
  }
  let html = '<div style="margin: 0 0 2px 0; line-height: 1.3;">';
  if (mealLabel) {
    html += `<strong style="font-weight: 700;">${mealLabel}</strong>`;
  }
  if (mealText) {
    html += `${mealLabel ? ": " : ""}${mealText}`;
  }
  html += "</div>";
  return html;
};

//  place Speak2 content  to calendar template
const placeSpeak2ContentToCalendarTempalte = (
  activetempalte,
  spea2data,
  CalendarTypes,
  selectedContentType = "Calendar"
) => {
  const isMenuContent = spea2data.length > 0 && spea2data[0].ContentType === "Menu";
  if (isMenuContent) {
    spea2data = handeMenuContentMealTypeAll(spea2data);
  }
  const headingLabel = getTabHeaderLabel(activetempalte, selectedContentType);
  const count = getMaxEventsFromSpeak2Content(spea2data);
  if (activetempalte.templateData?.CalendarHeader?.Heading?.HeadingText) {
    activetempalte.templateData.CalendarHeader.Heading.HeadingText.text = headingLabel;
  }
  let fontsize = 8;
  if (
    activetempalte.templateSize.widthInPixels === 1056 &&
    activetempalte.templateSize.heightInPixels === 816
  ) {
    // 11 by 8.5 inches
    fontsize = count <= 5 ? 10 : count <= 10 ? 7 : 6.5;

    // set Calendar type font size,
    if (activetempalte.isWeekView === false) {
      activetempalte.templateData.CalendarHeader.Heading.HeadingText.fontSize = 9;
    }

    // Calendar Header Date Text
    activetempalte.templateData.CalendarHeader.Heading.DateText.fontSize = 30;
    // Description font size
    activetempalte.templateData.CalendarHeader.Description.fontSize = 8;

    for (let dayitem of activetempalte.templateData.CalendarBody.calendarTableHeader) {
      dayitem.fontSize = 9;
    }
  } else {
    // 17 by 11 inches
    fontsize = count <= 5 ? 12 : count <= 10 ? 10 : 8;

    // set Calendar type font size,
    if (activetempalte.isWeekView === false) {
      activetempalte.templateData.CalendarHeader.Heading.HeadingText.fontSize = 14;
    }
    // Calendar Header Date Text
    activetempalte.templateData.CalendarHeader.Heading.DateText.fontSize = 50;
    // Description font size
    activetempalte.templateData.CalendarHeader.Description.fontSize = 12;

    for (let dayitem of activetempalte.templateData.CalendarBody.calendarTableHeader) {
      dayitem.fontSize = 14;
    }
  }

  for (let Speak2Item of spea2data) {
    for (let weekitem of activetempalte.templateData.CalendarBody.calendarTableRows) {
      for (let Cellitem of weekitem) {
        if (Cellitem.cellDate !== null) {
          const formattedDate = moment(Cellitem.cellDate, "M/D/YYYY").format("YYYY-MM-DD");
          const speak2Date = moment.unix(Speak2Item.DateAdded / 1000).format("YYYY-MM-DD");
          if (formattedDate === speak2Date) {
            if (Speak2Item.ContentType === "Calendar") {
              const formattedTime = formatEventTime(Speak2Item.time);
              const eventTitle = escapeHtml(Speak2Item.text || "");
              const eventLocation = escapeHtml(normalizeLocation(Speak2Item.asset));
              let text = "<div style=\"margin: 0 0 2px 0; line-height: 1.3;\">";
              if (formattedTime) {
                text += `<strong style="font-weight: 700;">${escapeHtml(formattedTime)}</strong>`;
              }
              if (eventTitle) {
                text += `${formattedTime ? " " : ""}${eventTitle}`;
              }
              if (eventLocation) {
                text += `${eventTitle ? ", " : ""}<em>${eventLocation}</em>`;
              }
              text += "</div>";
              Cellitem.cellText = Cellitem.cellText + text;
            } else {
              const menuHtml = buildMenuCellHtml(Speak2Item.MealType, Speak2Item.MealsList);
              if (menuHtml) {
                Cellitem.cellText = Cellitem.cellText + menuHtml;
              }
            }

            Cellitem.cellStyles.fontSize = fontsize;
            if (Speak2Item.ContentType === "Menu") {
              Cellitem.cellStyles.lineHeight = 1.3;
            }
          }
        }
      }
    }
  }
  return activetempalte;
};

// place Spek2 content to Slide/Document templates
const placeSpeak2ContentToSlideTemplate = (
  activetempalte,
  spea2data,
  selectedContentType = "Calendar",
  templateMeta = null
) => {
  const pageElements = activetempalte.templateData?.[0]?.Elements || [];
  const hasQptLayout = pageElements.some((e) => e.id?.startsWith("qpt-"));
  let customHeading = null;
  if (hasQptLayout) {
    const extractedMeta = extractQuickPrintHeaderMeta(pageElements);
    if (extractedMeta.headerElements?.length > 0) {
      activetempalte._extractedQuickPrintMeta = extractedMeta;
    }
    if (extractedMeta.heading && extractedMeta.heading.trim().length > 0) {
      customHeading = extractedMeta.heading.trim();
    }
  }
  // The print/preview flow renders content on hardcoded built-in template
  // objects that never carry `qpt-*` element ids, so the extraction above
  // never fires there. In that flow the user's saved custom heading is only
  // available via the selected template's persisted metadata.
  if (!customHeading && templateMeta?.heading && templateMeta.heading.trim().length > 0) {
    customHeading = templateMeta.heading.trim();
  }
  activetempalte.templateData[0].Elements = activetempalte.templateData[0].Elements.slice(0, 3);
  const headingElem = activetempalte.templateData[0].Elements.find(
    (el) => el.ElementType === "Heading"
  );
  const headingLabel = customHeading || getTabHeaderLabel(activetempalte, selectedContentType);
  if (headingElem) {
    headingElem.ElementData = headingLabel;
  }
  if (spea2data.length > 0 && spea2data[0].ContentType === "Calendar") {
    let latestTop = 270;
    // spea2data = spea2data.flatMap(item => [item, item]);
    const fontSize = spea2data.length > 12 ? 16 : spea2data.length > 6 ? 20 : 30;
    const rowgap = spea2data.length > 12 ? 5 : spea2data.length > 6 ? 10 : 15;
    const ElementHeight = spea2data.length > 12 ? 30 : null;
    const rowGap = rowgap;
    const rowHeight =
      ElementHeight !== null ? ElementHeight : CalendarGroup[0].Styles[0].ElementHeight;
    const ElementGroups = [];

    for (const speak2item of spea2data) {
      // clone + customise both templates into one row
      const rowClones = CalendarGroup.map((template) => {
        // deep‐clone so we don’t overwrite the originals
        const el = JSON.parse(JSON.stringify(template));

        // inject time or text
        if (el.IsTimeField) {
          el.ElementData = `<p><strong style="font-weight: 900;">${speak2item.time}</strong></p>`;
        } else {
          el.ElementData = `<p>${speak2item.text}, ${speak2item.asset}</p>`;
        }

        // position the row
        el.Styles[0].ElementTop = latestTop;
        el.Styles[0].ElementFontSize = fontSize;
        el.Styles[0].ElementHeight =
          ElementHeight !== null ? ElementHeight : el.Styles[0].ElementHeight;

        return el;
      });
      // collect them and bump the top for the next row
      ElementGroups.push(...rowClones);
      latestTop += rowHeight + rowGap;
    }
    // now push all at once
    activetempalte.templateData[0].Elements.push(...ElementGroups);

    // 1. Adjust Line
    const lineElem = activetempalte.templateData[0].Elements.find(
      (el) => el.ElementType === "Line"
    );
    if (lineElem) {
      lineElem.Styles.forEach((style) => {
        style.ElementTop = style.ElementTop + 50;
      });
    }

    // 2. Clone Heading
    const headingElem = activetempalte.templateData[0].Elements.find(
      (el) => el.ElementType === "Heading"
    );
    if (headingElem) {
      headingElem.ElementData = headingLabel;
      // Deep-clone the Styles array and shift positions
      const newStyles = headingElem.Styles.map((style) => ({
        ...style,
        ElementTop: style.ElementTop + style.ElementHeight,
        ElementHeight: 40,
        ElementFontSize: 16,
        ElementFontWeight: 400,
      }));

      const clonedHeading = {
        ...headingElem,
        ElementData: moment(activetempalte.startDate, ["YYYY-MM-DD", "MM/DD/YYYY"]).format(
          "dddd, M/D/YYYY"
        ),
        Styles: newStyles,
      };

      // 3. Insert it wherever you like—here we push to the end
      activetempalte.templateData[0].Elements.push(clonedHeading);
    }
  } else {
    // Menu content should only be applied when the Menu tab is selected.
    if (selectedContentType !== "Menu") {
      // Calendar tab with no calendar rows — keep an empty dailyEvents list so the
      // QP header/meta path still renders (instead of falling back to element layout).
      activetempalte.dailyHeading = headingLabel.toUpperCase();
      activetempalte.dailyEvents = [];
      activetempalte.speak2RawData = JSON.parse(JSON.stringify(spea2data || []));
      return activetempalte;
    }
    if (spea2data.length > 0 && spea2data[0].ContentType === "Menu") {
      spea2data = handeMenuContentMealTypeAll(spea2data);
      spea2data = mergeMealTypes(spea2data);
    }
    const rowGap = 10; // px between menu rows
    const templates = MenuGroup;
    const baseTop = templates[0].Styles[0].ElementTop + 20;

    // 1. compute each template item’s internal offset from the group’s baseTop
    const offsets = templates.map((t) => t.Styles[0].ElementTop - baseTop);

    // 2. compute groupHeight = max(offset + height) across all items
    const heights = templates.map((t) => t.Styles[0].ElementHeight);
    const groupHeight = Math.max(...offsets.map((o, i) => o + heights[i]));

    let groupTop = baseTop; // start where your first group sits
    const allEls = [];

    for (const row of spea2data) {
      // clone + stamp each template
      templates.forEach((tmpl, idx) => {
        const el = JSON.parse(JSON.stringify(tmpl));

        // inject dynamic content
        if (el.isMealField) {
          el.ElementData = `<p><strong>${row.MealType}</strong></p>`;
        } else {
          el.ElementData = `<p>${row.MealsList}</p>`;
        }

        // position it in this row
        el.Styles[0].ElementTop = groupTop + offsets[idx];

        allEls.push(el);
      });

      // move down for the next group
      groupTop += groupHeight + rowGap;
    }

    // finally, push them all in one go:
    activetempalte.templateData[0].Elements.push(...allEls);
    for (let elementitem of activetempalte.templateData[0].Elements) {
      if (elementitem.ElementType === "Heading") {
        elementitem.ElementData = headingLabel;
        break;
      }
    }

    // 1. Adjust Line
    const lineElem = activetempalte.templateData[0].Elements.find(
      (el) => el.ElementType === "Line"
    );
    if (lineElem) {
      lineElem.Styles.forEach((style) => {
        style.ElementTop = style.ElementTop + 50;
      });
    }

    // 2. Clone Heading
    const headingElem = activetempalte.templateData[0].Elements.find(
      (el) => el.ElementType === "Heading"
    );
    if (headingElem) {
      headingElem.ElementData = headingLabel;
      // Deep-clone the Styles array and shift positions
      const newStyles = headingElem.Styles.map((style) => ({
        ...style,
        ElementTop: style.ElementTop + style.ElementHeight,
        ElementHeight: 40,
        ElementFontSize: 16,
        ElementFontWeight: 400,
      }));

      const clonedHeading = {
        ...headingElem,
        ElementData: moment(activetempalte.startDate, ["YYYY-MM-DD", "MM/DD/YYYY"]).format(
          "dddd, M/D/YYYY"
        ),
        Styles: newStyles,
      };

      // 3. Insert it wherever you like—here we push to the end
      activetempalte.templateData[0].Elements.push(clonedHeading);
    }
  }
  //  add the raw speak2 data to active template for word export for daily templates.
  activetempalte.speak2RawData = JSON.parse(JSON.stringify(spea2data));

  // store structured daily events for DailyCalendarPrint component
  activetempalte.dailyHeading = headingLabel.toUpperCase();
  if (spea2data.length > 0 && spea2data[0].ContentType === "Calendar") {
    activetempalte.dailyEvents = spea2data.map((item) => ({
      time: item.time || "",
      name: item.text ? item.text.replace(/‌/g, "").trim() : "",
      shortDescription: normalizePrintDescription(item.shortDescription),
      longDescription: normalizePrintDescription(item.longDescription),
      location: normalizeLocation(item.asset) || null,
    }));
  }
  if (spea2data.length > 0 && spea2data[0].ContentType === "Menu") {
    activetempalte.dailyEvents = spea2data.map((item) => ({
      time: item.MealType || "",
      name: item.MealsList || "",
      shortDescription: null,
      longDescription: null,
      location: null,
    }));
    activetempalte.isMenuContent = true;
  }

  return activetempalte;
};

// get the maximum events from calendar speak2 content,
const getMaxEventsFromSpeak2Content = (speak2data) => {
  if (speak2data.length === 0) {
    return 0;
  }
  const groups = speak2data.reduce((acc, item) => {
    const key = moment.unix(item.DateAdded / 1000).format("YYYY-MM-DD");
    (acc[key] = acc[key] || []).push(item);
    return acc;
  }, {});

  const maxEvents = Math.max(...Object.values(groups).map((group) => group.length));
  return maxEvents;
};
